/**
 * Synchronous Sentry initialization. Imported FIRST in main.tsx so module-load
 * errors in subsequent imports (App, layer, routes) are captured.
 *
 * DSN is selected from build-time `import.meta.env.VITE_ENV`. Runtime
 * `config.json` may later override the environment tag in main.tsx via
 * `Sentry.setTag` — but the SDK must be initialized before any other import
 * runs, so we cannot wait for `config.json` to load.
 *
 * Also exposes the SDK on `window.Sentry` so the backoffice layer's
 * ErrorBoundary fallbacks can forward caught errors without taking a
 * direct dependency on `@sentry/*`.
 */

import * as Sentry from '@sentry/react';

// Build-time constants injected via Vite's `define` (vite.config.ts).
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle
declare const __BACKOFFICE_LAYER_VERSION__: string;
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle
declare const __APP_VERSION__: string;

const SENTRY_DSN_PROD =
  'https://38d9bf9bf8a8c469f05c1bd7802623ed@o4511529288531968.ingest.de.sentry.io/4511534873378896';
const SENTRY_DSN_NON_PROD =
  'https://5562773793e553a955b8d8ea82b98e00@o4511529288531968.ingest.de.sentry.io/4511534869315664';

/** Dev/staging opt-in: set to 'true' in Application → Local Storage to enable Sentry. Prod ignores this flag. */
const SENTRY_OPT_IN_KEY = 'FASTLESSJS_SENTRY_ENABLED';

const buildEnv = String(import.meta.env.VITE_ENV ?? '').toLowerCase();
const dsn = buildEnv === 'prod' ? SENTRY_DSN_PROD : SENTRY_DSN_NON_PROD;
const environment = buildEnv || 'production';

const isEnabled = (() => {
  if (!dsn) return false;
  if (buildEnv === 'prod') return true;
  return (
    typeof window !== 'undefined' &&
    window.localStorage?.getItem(SENTRY_OPT_IN_KEY) === 'true'
  );
})();

if (isEnabled) {
  const appVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown';
  const layerVersion =
    typeof __BACKOFFICE_LAYER_VERSION__ !== 'undefined'
      ? __BACKOFFICE_LAYER_VERSION__
      : 'unknown';

  Sentry.init({
    dsn,
    release: `rm-front@${appVersion}`,
    environment,
    initialScope: {
      tags: {
        'backoffice-layer.version': layerVersion,
        'app.version': appVersion,
      },
    },
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
      Sentry.replayIntegration(),
      Sentry.httpClientIntegration({ failedRequestStatusCodes: [[500, 599]] }),
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
      Sentry.extraErrorDataIntegration({ depth: 5 }),
    ],
    tracesSampleRate: 1.0,
    profilesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    ignoreErrors: [
      /Failed to fetch dynamically imported module/i,
      /Importing a module script failed/i,
      /error loading dynamically imported module/i,
      'ChunkLoadError',
      /HTTP Client Error with status code: 401/i,
      /Session expired or unauthorized/i,
      /Your session has expired/i,
      /Error status: 401/i,
      /Error message: Unauthorized/i,
      /API call failed: (GET|POST|PUT|DELETE|PATCH)/i,
      /Error response: null/i,
    ],
    beforeSend(event, hint) {
      const exception = hint?.originalException as unknown;
      const message =
        (typeof exception === 'object' && exception !== null && 'message' in exception
          ? String((exception as { message?: unknown }).message ?? '')
          : '') ||
        event.message ||
        '';
      if (/\b401\b|session has expired|signing out|unauthorized/i.test(message)) {
        return null;
      }
      return event;
    },
  });

  if (typeof window !== 'undefined') {
    (window as Window & { Sentry?: typeof Sentry }).Sentry = Sentry;
  }
}

export const sentryEnabled = isEnabled;
